import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, useLayoutSize } from '../../theme';
import { iconSize, s } from '../../utils/libs';
import { WEB_NAV_SIDEBAR_RAIL_S } from '../../theme/webLayoutWidths';
import { PRODUCT_LIST_TOP_BAR_CONTENT_MIN_HEIGHT } from '../../CONSTANTS';
import Typography from '../Reusables/Typography';

export type WebHeaderBarProps = {
  onBack?: () => void;
  /** Content rendered after the back button slot (search bars, labels). */
  children?: React.ReactNode;
  /** Title — string or custom component. */
  title?: string | React.ReactNode;
  /** 'left' (default) or 'center'. */
  titleAlign?: 'left' | 'center';
  /** Right side actions. */
  rightSlot?: React.ReactNode;
  /** Whether to show the back button. Default true. */
  showBack?: boolean;
};

/**
 * Shared sidebar header bar that aligns the back button with the sidebar rail.
 * Returns null when the sidebar layout is not active.
 */
export default function WebHeaderBar({
  onBack,
  children,
  title,
  titleAlign = 'left',
  rightSlot,
  showBack = true,
}: WebHeaderBarProps) {
  const { useSidebarNav } = useLayoutSize();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => createStyles(theme), [theme]);

  if (!useSidebarNav) return null;

  const titleNode =
    typeof title === 'string' ? (
      <Typography style={styles.titleText} numberOfLines={1}>
        {title}
      </Typography>
    ) : (
      (title ?? null)
    );

  return (
    <View
      style={[
        styles.bar,
        {
          paddingTop: insets.top,
          minHeight: PRODUCT_LIST_TOP_BAR_CONTENT_MIN_HEIGHT + insets.top,
        },
      ]}>
      {/* Left area: back slot + children (search bars etc.) */}
      <View style={styles.left}>
        {showBack ? (
          <View style={styles.backSlot}>
            <Pressable
              onPress={onBack}
              accessibilityRole="button"
              accessibilityLabel="Go back"
              style={({ hovered }: any) => [
                styles.backButton,
                hovered && styles.backButtonHovered,
              ]}>
              <Ionicons
                name="chevron-back-outline"
                size={iconSize(22)}
                color="#fff"
              />
            </Pressable>
          </View>
        ) : (
          <View style={styles.backSlot} />
        )}
        {children}
      </View>

      {/* Center title (absolute when centered) */}
      {titleNode ? (
        titleAlign === 'center' ? (
          <View style={styles.titleCenter} pointerEvents="box-none">
            {titleNode}
          </View>
        ) : (
          <View style={styles.titleLeft}>{titleNode}</View>
        )
      ) : null}

      {/* Right slot */}
      {rightSlot ? <View style={styles.right}>{rightSlot}</View> : null}
    </View>
  );
}

const createStyles = (theme: any) =>
  StyleSheet.create({
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: 0,
      paddingRight: s(8),
      backgroundColor: theme.colors.primaryDark,
    },
    left: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      zIndex: 1,
    },
    backSlot: {
      width: s(WEB_NAV_SIDEBAR_RAIL_S),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    backButton: {
      width: s(36),
      height: s(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      flexShrink: 0,
    } as any,
    backButtonHovered: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    titleLeft: {
      flex: 1,
      marginLeft: s(4),
      zIndex: 0,
    },
    titleCenter: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      pointerEvents: 'box-none',
    } as any,
    titleText: {
      color: '#fff',
      fontSize: 15,
      fontWeight: '600',
    },
    right: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      flexShrink: 0,
      zIndex: 1,
    },
  });
